import { useCallback, useRef, useState } from 'react';

import './hype.css';

/**
 * The applause meter, and the sparks it throws.
 *
 * ## Two ways to press it, one number
 *
 * The button is here, and on the stage the venue itself is tappable - see
 * `LiveStage`. Both call the same `tap`, and both throw their own sparks from
 * wherever they were pressed, because a spark that appeared somewhere other
 * than where a finger landed reads as a glitch rather than as a response.
 *
 * ## Nothing here waits for anything
 *
 * A press paints a spark and bumps the count in the same frame. The request
 * that eventually tells the server is a second away and carries a dozen of
 * these at once - see `useHype`.
 */

/**
 * What comes off a tap.
 *
 * Mostly fire, because that is what people mean by it, with the horns and the
 * sparkle turning up often enough to keep a long burst from reading as one
 * repeated image.
 */
const GLYPHS = ['🔥', '🔥', '🔥', '🔥', '🤘', '✨'];

/**
 * How many sparks can be in the air at once.
 *
 * Somebody holding the button makes about nine a second and each lives just
 * over a second, so this is roughly three times what enthusiasm produces - and
 * a hard stop on what a stuck key produces.
 */
const MAX_SPARKS = 36;

interface Spark {
  id: number;
  x: number;
  y: number;
  drift: number;
  glyph: string;
}

/**
 * A little emitter: somewhere to put sparks, and a way to throw one.
 *
 * Shared because the stage's tap surface throws the same sparks from the same
 * animation, and two implementations of "an emoji floats up and fades" is two
 * things to keep agreeing with each other.
 */
export function useSparks(max = MAX_SPARKS) {
  const [sparks, setSparks] = useState<Spark[]>([]);
  const next = useRef(0);

  const burst = useCallback(
    (x: number, y: number) => {
      next.current += 1;

      const spark: Spark = {
        id: next.current,
        x,
        y,
        // Each one leans somewhere different, or a burst is a column.
        drift: Math.round(Math.random() * 64 - 32),
        glyph: GLYPHS[Math.floor(Math.random() * GLYPHS.length)] ?? '🔥',
      };

      setSparks((current) => [...current.slice(-(max - 1)), spark]);
    },
    [max],
  );

  const done = useCallback((id: number) => {
    setSparks((current) => current.filter((spark) => spark.id !== id));
  }, []);

  return { sparks, burst, done };
}

/**
 * Where sparks are drawn: absolutely, above everything, catching nothing.
 *
 * `pointer-events-none` matters more than it looks. This layer sits over the
 * thing that spawned it, and sparks that swallowed clicks would mean a fast
 * tapper's third press landed on a floating emoji instead of the button.
 */
export function SparkLayer({
  sparks,
  onDone,
  size = 'text-2xl',
}: {
  sparks: Spark[];
  onDone: (id: number) => void;
  size?: string;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {sparks.map((spark) => (
        <span
          key={spark.id}
          data-hype-spark
          onAnimationEnd={() => onDone(spark.id)}
          className={`hype-spark absolute select-none leading-none ${size}`}
          style={{
            left: spark.x,
            top: spark.y,
            ['--drift' as string]: `${spark.drift}px`,
            translate: '-50% -50%',
          }}
        >
          {spark.glyph}
        </span>
      ))}
    </div>
  );
}

/**
 * The button and the number, for wherever the room's controls are.
 *
 * The count is `tabular-nums` because it changes several times a second and
 * proportional digits make it jitter sideways - which turns a number climbing
 * into a number twitching.
 */
export function HypeMeter({
  total,
  onTap,
  disabled = false,
  compact = false,
}: {
  total: number;
  onTap: () => void;
  /** No room to join, so no button - listening is what makes it yours to press. */
  disabled?: boolean;
  /** Beside a strip of characters rather than under a venue. */
  compact?: boolean;
}) {
  const { sparks, burst, done } = useSparks();
  const [pressed, setPressed] = useState(0);
  const box = useRef<HTMLDivElement | null>(null);

  const press = () => {
    if (disabled) return;

    const bounds = box.current?.getBoundingClientRect();

    burst(bounds ? bounds.width / 2 : 0, bounds ? bounds.height - 12 : 0);
    setPressed((n) => n + 1);
    onTap();
  };

  return (
    <div className="relative flex items-center gap-2" ref={box} data-hype>
      <button
        type="button"
        data-hype-tap
        disabled={disabled}
        onClick={press}
        aria-label="Hype this room"
        title={disabled ? 'Listen live to join in' : 'Hype this room'}
        className={`flex items-center justify-center rounded-[var(--radius-pill)] transition-colors disabled:opacity-40 ${
          compact ? 'h-8 w-8 text-base' : 'h-9 w-9 text-lg'
        } hover:bg-white/10`}
      >
        {/* Keyed on the press count so the animation restarts on every tap
            rather than only on the first. */}
        <span key={pressed} className="hype-press leading-none">
          🔥
        </span>
      </button>

      <span
        data-hype-total
        className="min-w-[3ch] font-mono text-[12px] tabular-nums text-soft"
      >
        {total.toLocaleString()}
      </span>

      <SparkLayer sparks={sparks} onDone={done} size={compact ? 'text-lg' : 'text-2xl'} />
    </div>
  );
}
